import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  supabase,
  supabaseAuthStorageKey,
  supabaseProjectRef,
  isSupabaseConfigured,
} from '../lib/supabase';
import {
  clearSupabaseAuthStorage,
} from '../lib/authSessionHelpers';
import {
  clearAllCachedProfiles,
  clearCachedProfile as clearPersistedCachedProfile,
  readCachedProfile as readPersistedCachedProfile,
  writeCachedProfile as persistCachedProfile,
} from '../lib/publicSessionCache';
import { verifiedProfilesCache } from '../lib/verifiedProfilesCache';
import crossWindowSync from '../utils/crossWindowSync';
import { resolveAccessState } from '../utils/accessState';
import {
  clearAllApBioOnboardingStorage,
  clearApBioOnboardingUxState,
} from '../lib/apBioOnboarding';
import {
  clearAuthRedirectFlags,
  clearAuthFlowMarker,
  clearPaymentFlags,
  clearPrePaymentMarkers,
  clearSignedOutAt,
  isSignedOutMarkerActive,
  setAuthFlowMarker,
  setSignedOutAt
} from '../utils/authFlow';
import { needsOnboarding } from '../utils/onboardingRules';
import { createProfileIfMissing } from '../utils/profileCreation';
import {
  isLocalPreviewMode,
  LOCAL_PREVIEW_PROFILE,
  LOCAL_PREVIEW_SUBSCRIPTION,
  LOCAL_PREVIEW_USER,
} from '../lib/localPreview';

console.log('[AuthContext.jsx] File loaded (outside provider)');

// Profile cache that lasts for entire session
const profileCache = new Map();

const readCachedProfile = (userId) => {
  if (!userId) return null;
  if (profileCache.has(userId)) return profileCache.get(userId);
  const cachedProfile = readPersistedCachedProfile(userId);
  if (cachedProfile) {
    profileCache.set(userId, cachedProfile);
    return cachedProfile;
  }
  return null;
};

const writeCachedProfile = (userId, profileData) => {
  if (!userId || !profileData) return;
  profileCache.set(userId, profileData);
  verifiedProfilesCache.set(userId, profileData);
  persistCachedProfile(userId, profileData, { source: 'auth_context' });
};

const clearCachedProfileForUser = (userId) => {
  if (!userId) return;
  profileCache.delete(userId);
  verifiedProfilesCache.delete(userId);
  clearPersistedCachedProfile(userId);
};

// Global debug utility for onboarding
window.debugOnboarding = {
  getCurrentProfile: () => {
    const user = window.supabaseClient?.auth?.user;
    if (!user) return null;
    return profileCache.get(user.id) || null;
  },
  getOnboardingStatus: () => {
    const profile = window.debugOnboarding.getCurrentProfile();
    if (!profile) return { error: 'No profile found' };

    const subscriptionType = profile.subscription_type?.toLowerCase();
    const onboardingCompleted = profile.onboarding_completed;
    const requiresOnboarding = needsOnboarding(profile);

    return {
      profile,
      subscriptionType,
      onboardingCompleted,
      needsOnboarding: requiresOnboarding,
      currentPath: window.location.pathname,
      shouldRedirect: requiresOnboarding && window.location.pathname !== '/onboarding'
    };
  },
  forceOnboardingCheck: () => {
    const status = window.debugOnboarding.getOnboardingStatus();
    console.log('ONBOARDING DEBUG STATUS:', status);
    if (status.shouldRedirect) {
      console.log('FORCING ONBOARDING REDIRECT');
      window.location.href = '/onboarding';
    }
    return status;
  }
};

const AuthContext = createContext({});

const isPreviewUserId = (userId) => userId === LOCAL_PREVIEW_USER.id;
const isProfileHydrationPending = (status) => status === 'loading' || status === 'cached';
const isSubscriptionHydrationPending = (status) => status === 'loading';
const isInvalidAuthError = (error) => {
  const status = Number(error?.status);
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  return status === 401
    || code === 'pgrst301'
    || message.includes('no suitable key')
    || message.includes('wrong key type')
    || message.includes('invalid jwt')
    || message.includes('jwt expired');
};

export const AuthProvider = ({ children }) => {
  console.log('[AuthContext] AuthProvider rendering...');

  const isMounted = useRef(true);
  const initialSessionCheckedRef = useRef(false);
  const lastHydratedUserIdRef = useRef(null);
  const hydrationGenerationRef = useRef(0);
  const activeHydrationRef = useRef({ userId: null, generation: 0 });
  const lastLoggedMismatchRef = useRef(null);
  const recentCallbackHydrationRef = useRef({ userId: null, at: 0 });
  const invalidSessionPromiseRef = useRef(null);
  const authStateRef = useRef({
    user: null,
    profile: null,
    subscription: null,
    remainingPrompts: 0,
    profileStatus: 'idle',
    subscriptionStatus: 'idle',
  });

  const localPreviewMode = isLocalPreviewMode();
  const [user, setUser] = useState(localPreviewMode ? LOCAL_PREVIEW_USER : null);
  const [profile, setProfile] = useState(localPreviewMode ? LOCAL_PREVIEW_PROFILE : null);
  const [loading, setLoading] = useState(!localPreviewMode);
  const [subscription, setSubscription] = useState(localPreviewMode ? LOCAL_PREVIEW_SUBSCRIPTION : null);
  const [remainingPrompts, setRemainingPrompts] = useState(localPreviewMode ? LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts : 0);
  const [profileStatus, setProfileStatus] = useState(localPreviewMode ? 'ready' : 'idle');
  const [subscriptionStatus, setSubscriptionStatus] = useState(localPreviewMode ? 'ready' : 'idle');

  // Function to broadcast auth state changes to other tabs and windows
  const broadcastAuthChange = (type, data = {}) => {
    try {
      const authData = {
        user: data.user || null,
        profile: data.profile || null,
        subscription: data.subscription || null,
        remainingPrompts: data.remainingPrompts || 0
      };

      crossWindowSync.broadcastAuthChange(type, authData);
    } catch (error) {
      console.error('[AuthContext] Error broadcasting auth change:', error);
    }
  };

  // Safe state setters that check mounting status
  const safeSetUser = (data) => {
    if (isMounted.current) setUser(data);
  };

  const safeSetProfile = (data) => {
    if (isMounted.current) setProfile(data);
  };

  const safeSetLoading = (isLoading) => {
    if (isMounted.current) setLoading(isLoading);
  };

  const safeSetSubscription = (data) => {
    if (isMounted.current) setSubscription(data);
  };

  const safeSetRemainingPrompts = (count) => {
    if (isMounted.current) setRemainingPrompts(count);
  };

  const safeSetProfileStatus = (nextStatus) => {
    if (isMounted.current) setProfileStatus(nextStatus);
  };

  const safeSetSubscriptionStatus = (nextStatus) => {
    if (isMounted.current) setSubscriptionStatus(nextStatus);
  };

  const invalidateHydration = (nextUserId = null) => {
    hydrationGenerationRef.current += 1;
    activeHydrationRef.current = {
      userId: nextUserId,
      generation: hydrationGenerationRef.current,
    };
    return activeHydrationRef.current;
  };

  const beginHydrationForUser = (userId, { provisionalProfile = null } = {}) => {
    const token = invalidateHydration(userId);

    safeSetProfile(provisionalProfile);
    safeSetSubscription(null);
    safeSetRemainingPrompts(0);
    safeSetProfileStatus(provisionalProfile ? 'cached' : 'loading');
    safeSetSubscriptionStatus('loading');

    return token;
  };

  const isHydrationCurrent = (userId, generation) => (
    activeHydrationRef.current.userId === userId &&
    activeHydrationRef.current.generation === generation
  );

  useEffect(() => {
    authStateRef.current = {
      user,
      profile,
      subscription,
      remainingPrompts,
      profileStatus,
      subscriptionStatus,
    };
  }, [profileStatus, profile, remainingPrompts, subscriptionStatus, subscription, user]);

  // Cached profile data can make the shell feel fast, but it must never grant
  // paid access. Entitlements fail closed until the current account has been
  // verified against Supabase during this provider lifetime.
  const profileEffectivelyFresh = profileStatus === 'ready';
  const subscriptionEffectivelyFresh = subscriptionStatus === 'ready';

  const accessState = resolveAccessState({
    user,
    profile,
    subscription,
    loading,
    profileFresh: profileEffectivelyFresh,
    subscriptionFresh: subscriptionEffectivelyFresh,
  });

  useEffect(() => {
    if (!user?.id || !accessState.mismatch) return;

    const mismatchKey = JSON.stringify({
      userId: user.id,
      mismatch: accessState.mismatch,
    });

    if (lastLoggedMismatchRef.current === mismatchKey) return;
    lastLoggedMismatchRef.current = mismatchKey;

    console.warn('[AuthContext] Subscription/profile mismatch detected', {
      userId: user.id,
      mismatch: accessState.mismatch,
      profileSubscriptionType: profile?.subscription_type || null,
      subscriptionPlanType: subscription?.plan_type || null,
      subscriptionStatus: subscription?.status || null,
    });
  }, [accessState.mismatch, profile?.subscription_type, subscription?.plan_type, subscription?.status, user?.id]);

  // Expose client for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthContext: Exposing Supabase client to window.supabaseClient for debugging');
      window.supabaseClient = supabase;
    }
  }, []);

  const clearAuthState = () => {
    if (localPreviewMode) {
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      lastHydratedUserIdRef.current = LOCAL_PREVIEW_USER.id;
      profileCache.set(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      verifiedProfilesCache.set(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      invalidateHydration(LOCAL_PREVIEW_USER.id);
      return;
    }

    invalidateHydration(null);
    safeSetUser(null);
    safeSetProfile(null);
    safeSetSubscription(null);
    safeSetRemainingPrompts(0);
    safeSetProfileStatus('idle');
    safeSetSubscriptionStatus('idle');
    safeSetLoading(false);
    lastHydratedUserIdRef.current = null;
    profileCache.clear();
    verifiedProfilesCache.clear();
    clearAllCachedProfiles();
  };

  const invalidateSessionFromAuthError = (error) => {
    if (!isInvalidAuthError(error)) return Promise.resolve(false);
    if (invalidSessionPromiseRef.current) return invalidSessionPromiseRef.current;

    invalidSessionPromiseRef.current = (async () => {
      setSignedOutAt();
      clearAuthRedirectFlags();
      clearAuthFlowMarker();
      clearPrePaymentMarkers();
      clearPaymentFlags();

      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutError) {
        console.warn('[AuthContext] Local sign-out after invalid session failed:', signOutError);
      }

      clearSupabaseAuthStorage(supabaseProjectRef);
      clearAuthState();
      broadcastAuthChange('logout');
      return true;
    })().finally(() => {
      invalidSessionPromiseRef.current = null;
    });

    return invalidSessionPromiseRef.current;
  };

  const getStoredAuthUser = () => {
    try {
      if (isSignedOutMarkerActive()) return null;

      const storedAuth = localStorage.getItem(supabaseAuthStorageKey);
      if (!storedAuth) return null;
      const authData = JSON.parse(storedAuth);
      const session = authData?.currentSession || authData?.session || authData;

      if (!session.user || !session.access_token || !session.refresh_token) return null;
      const isExpired = session.expires_at && session.expires_at * 1000 < Date.now();
      if (isExpired) return null;

      return session.user;
    } catch (error) {
      console.error('[AuthContext] Error checking localStorage for auth token:', error);
      return null;
    }
  };

  async function fetchUserSubscription(
    userId,
    {
      generation = activeHydrationRef.current.generation,
      updateState = true,
      markLoading = updateState,
    } = {},
  ) {
    if (!userId || !isMounted.current) return null;
    if (localPreviewMode && isPreviewUserId(userId)) {
      if (updateState && isHydrationCurrent(userId, generation)) {
        safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
        safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
        safeSetSubscriptionStatus('ready');
      }
      return LOCAL_PREVIEW_SUBSCRIPTION;
    }

    if (updateState && markLoading && isHydrationCurrent(userId, generation)) {
      safeSetSubscriptionStatus('loading');
    }

    console.log(`[AuthContext] Fetching subscription for user: ${userId}`);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['trialing', 'active'])
        .maybeSingle();

      if (!isMounted.current) return null;

      if (error) {
        console.error('Error fetching subscription:', error);
        void invalidateSessionFromAuthError(error);
        if (updateState && isHydrationCurrent(userId, generation)) {
          safeSetSubscription(null);
          safeSetRemainingPrompts(0);
          safeSetSubscriptionStatus('error');
        }
        return null;
      }

      if (updateState && isHydrationCurrent(userId, generation)) {
        safeSetSubscription(data);
        if (data) {
          safeSetRemainingPrompts(data.remaining_prompts ?? 0);
        } else {
          safeSetRemainingPrompts(0);
        }
        safeSetSubscriptionStatus('ready');
      }

      return data;
    } catch (error) {
      console.error('AuthContext: Exception fetching subscription:', error);
      if (updateState && isMounted.current && isHydrationCurrent(userId, generation)) {
        safeSetSubscription(null);
        safeSetRemainingPrompts(0);
        safeSetSubscriptionStatus('error');
      }
      return null;
    }
  }

  const getOrCreateProfile = async (
    userId,
    sessionUser,
    { forceRefresh = false, onProfileBootstrapEvent } = {}
  ) => {
    if (!userId) return null;
    if (localPreviewMode && isPreviewUserId(userId)) {
      writeCachedProfile(userId, LOCAL_PREVIEW_PROFILE);
      return LOCAL_PREVIEW_PROFILE;
    }

    if (!forceRefresh) {
      const cached = readCachedProfile(userId);
      if (cached) return cached;
    }

    try {
      const { data: fetchedProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!isMounted.current) return null;

      if (fetchError) {
        console.error('[AuthContext] Error fetching profile:', fetchError);
        void invalidateSessionFromAuthError(fetchError);
        return null;
      }

      if (fetchedProfile) {
        writeCachedProfile(userId, fetchedProfile);
        return fetchedProfile;
      }

      const userEmail = sessionUser?.email;
      if (!userEmail) {
        const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
        if (userError || !currentUser || currentUser.id !== userId) {
          console.error('[AuthContext] Error fetching current user for profile creation:', userError);
          return null;
        }
        if (!currentUser.email) {
          console.error('[AuthContext] Cannot create profile: User email is missing.');
          return null;
        }

        return await createProfile(userId, currentUser.email, { onProfileBootstrapEvent });
      }

      return await createProfile(userId, userEmail, { onProfileBootstrapEvent });
    } catch (error) {
      console.error(`[AuthContext] Error in getOrCreateProfile for ${userId}:`, error);
      return null;
    }
  };

  const createProfile = async (userId, email, { onProfileBootstrapEvent } = {}) => {
    try {
      const newProfile = await createProfileIfMissing({
        supabaseClient: supabase,
        userId,
        email,
        onStatus: onProfileBootstrapEvent
      });

      if (newProfile) {
        // Freshly bootstrapped profiles should replay onboarding tours for this user.
        clearAllApBioOnboardingStorage(userId);
        clearApBioOnboardingUxState(userId);
        writeCachedProfile(userId, newProfile);
      }

      return newProfile;
    } catch (error) {
      console.error('[AuthContext] Exception during profile creation:', error);
      return null;
    }
  };

  const hydrateFromSession = async (session, { broadcast = false } = {}) => {
    if (localPreviewMode) {
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      lastHydratedUserIdRef.current = LOCAL_PREVIEW_USER.id;
      writeCachedProfile(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      invalidateHydration(LOCAL_PREVIEW_USER.id);
      return;
    }

    if (!session?.user?.id) return;

    const currentUser = session.user;
    const userId = currentUser.id;
    const currentState = authStateRef.current;
    const currentStateUserId = currentState.user?.id;
    const sameUserProfile = currentState.profile?.id === userId
      ? currentState.profile
      : null;
    const currentProfileStatus = currentState.profileStatus;
    const currentSubscriptionStatus = currentState.subscriptionStatus;
    const provisionalProfile = sameUserProfile || readCachedProfile(userId);
    const sameUserHydrationPending =
      currentStateUserId === userId &&
      (
        isProfileHydrationPending(currentProfileStatus) ||
        isSubscriptionHydrationPending(currentSubscriptionStatus)
      );

    safeSetUser(currentUser);
    safeSetLoading(false);
    clearSignedOutAt();
    clearAuthRedirectFlags();

    if (broadcast) {
      broadcastAuthChange('login', {
        user: currentUser,
        profile: null,
        subscription: null,
        remainingPrompts: 0
      });
    }

    if (sameUserHydrationPending) {
      return;
    }

    // Skip re-fetching if AuthCallback just hydrated this user (within last 5s)
    const recentCb = recentCallbackHydrationRef.current;
    if (recentCb.userId === userId && (Date.now() - recentCb.at) < 5000) {
      const cachedProfile = readCachedProfile(userId);
      if (cachedProfile) {
        safeSetProfile(cachedProfile);
        safeSetProfileStatus('ready');

        // Use subscription data carried from AuthCallback if context doesn't have it yet
        const hasFreshCurrentSubscription = Boolean(
          currentState.subscriptionStatus === 'ready' &&
          currentState.subscription?.user_id === userId
        );
        const cbSub = recentCb.subscription;
        if (hasFreshCurrentSubscription) {
          return;
        }
        if (cbSub !== undefined && cbSub !== null) {
          safeSetSubscription(cbSub);
          safeSetRemainingPrompts(cbSub.remaining_prompts ?? 0);
          safeSetSubscriptionStatus('ready');
          return;
        }
        if (cbSub === null) {
          // AuthCallback fetched subscription but user has none — still mark as resolved
          safeSetSubscription(null);
          safeSetRemainingPrompts(0);
          safeSetSubscriptionStatus('ready');
          return;
        }
      }
    }

    const shouldResetResolvedAccess =
      currentStateUserId !== userId ||
      currentProfileStatus !== 'ready' ||
      currentSubscriptionStatus !== 'ready';
    const hydrationToken = shouldResetResolvedAccess
      ? beginHydrationForUser(userId, { provisionalProfile })
      : invalidateHydration(userId);

    lastHydratedUserIdRef.current = userId;

    getOrCreateProfile(userId, currentUser, { forceRefresh: true })
      .then((fetchedProfile) => {
        if (!isMounted.current || !isHydrationCurrent(userId, hydrationToken.generation)) return;

        if (fetchedProfile) {
          safeSetProfile(fetchedProfile);
          writeCachedProfile(userId, fetchedProfile);
        } else {
          clearCachedProfileForUser(userId);
          safeSetProfile(null);
        }

        safeSetProfileStatus('ready');
      })
      .catch((error) => {
        console.error('[AuthContext] Error fetching profile:', error);
        void invalidateSessionFromAuthError(error);
        if (!isMounted.current || !isHydrationCurrent(userId, hydrationToken.generation)) return;
        safeSetProfile(null);
        safeSetProfileStatus('error');
      });

    fetchUserSubscription(userId, {
      generation: hydrationToken.generation,
      updateState: true,
      markLoading: shouldResetResolvedAccess,
    }).catch((error) => {
      console.error('[AuthContext] Error fetching subscription:', error);
      void invalidateSessionFromAuthError(error);
    });
  };

  // Cross-window synchronization (single subscription)
  useEffect(() => {
    if (localPreviewMode) return undefined;

    const handleAuthStateRequest = () => {
      const currentState = authStateRef.current;
      if (!currentState.user) return;
      crossWindowSync.respondAuthState({
        user: currentState.user,
        profile: currentState.profile,
        subscription: currentState.subscription,
        remainingPrompts: currentState.remainingPrompts
      });
    };

    const applyRemoteAuthState = async (message) => {
      if (!message?.user) return;

      const currentUserId = authStateRef.current.user?.id;
      if (currentUserId && currentUserId === message.user.id) return;

      try {
        let confirmedSession = null;

        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user?.id === message.user.id) {
          confirmedSession = sessionData.session;
        } else {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshData?.session?.user?.id === message.user.id) {
            confirmedSession = refreshData.session;
          }
        }

        if (confirmedSession) {
          await hydrateFromSession(confirmedSession, { broadcast: false });
          return;
        }
      } catch (error) {
        console.error('[AuthContext] Error syncing remote auth state:', error);
      }

      console.warn('[AuthContext] Ignoring cross-window auth state without a verified session.');
    };

    const handleLogout = () => {
      const currentUserId = authStateRef.current.user?.id || null;
      if (currentUserId) {
        clearAllApBioOnboardingStorage(currentUserId);
      }
      clearAuthState();
      clearSupabaseAuthStorage(supabaseProjectRef);
      clearAuthFlowMarker();
      clearPrePaymentMarkers();
      clearPaymentFlags();
    };

    const unsubscribeRequest = crossWindowSync.subscribe('auth-state-request', handleAuthStateRequest);
    const unsubscribeResponse = crossWindowSync.subscribe('auth-state-response', applyRemoteAuthState);
    const unsubscribeLogin = crossWindowSync.subscribe('login', applyRemoteAuthState);
    const unsubscribeLogout = crossWindowSync.subscribe('logout', handleLogout);

    return () => {
      unsubscribeRequest();
      unsubscribeResponse();
      unsubscribeLogin();
      unsubscribeLogout();
    };
  }, [localPreviewMode]);

  // Effect to handle mount/unmount and auth state changes
  useEffect(() => {
    console.log('[AuthContext] Running main effect - initial check and listener setup');
    isMounted.current = true;
    safeSetLoading(true);

    if (localPreviewMode) {
      writeCachedProfile(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      lastHydratedUserIdRef.current = LOCAL_PREVIEW_USER.id;
      invalidateHydration(LOCAL_PREVIEW_USER.id);
      initialSessionCheckedRef.current = true;

      return () => {
        isMounted.current = false;
      };
    }

    const storedUser = getStoredAuthUser();
    if (storedUser) {
      void hydrateFromSession({ user: storedUser }, { broadcast: false });
    }

    const checkInitialSession = async () => {
      try {
        if (storedUser) {
          initialSessionCheckedRef.current = true;
          return;
        }

        const controller = new AbortController();
        const failSafe = setTimeout(() => controller.abort(), 2000);
        let session;
        let error;
        try {
          const response = await supabase.auth.getSession({ signal: controller.signal });
          session = response.data.session;
          error = response.error;
        } catch (fetchErr) {
          console.warn('[AuthContext] getSession timed-out or failed', fetchErr);
          session = null;
          error = fetchErr;
        } finally {
          clearTimeout(failSafe);
        }

        if (!isMounted.current) return;

        if (error) {
          console.error('[AuthContext] Error during initial getSession():', error);
          await invalidateSessionFromAuthError(error);
          clearAuthState();
        } else if (session) {
          await hydrateFromSession(session, { broadcast: false });
        } else {
          clearAuthState();
        }
      } catch (err) {
        console.error('[AuthContext] Critical error during initial session check:', err);
        clearAuthState();
      } finally {
        if (isMounted.current) {
          initialSessionCheckedRef.current = true;
          safeSetLoading(false);
          if (!storedUser) {
            crossWindowSync.requestAuthState();
          }
        }
      }
    };

    checkInitialSession();

    console.log('[AuthContext] Subscribing to onAuthStateChange');
    const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`[AuthContext] onAuthStateChange event: ${event}`, session);

        if (!initialSessionCheckedRef.current && isMounted.current) {
          initialSessionCheckedRef.current = true;
        }

        if (!isMounted.current) return;

        if (session?.user) {
          const shouldBroadcast = event === 'SIGNED_IN';
          await hydrateFromSession(session, { broadcast: shouldBroadcast });
        } else {
          clearAuthState();
          broadcastAuthChange('logout');
        }

        if (isMounted.current) {
          safeSetLoading(false);
        }
      }
    );

    return () => {
      console.log('[AuthContext] Cleaning up main effect - unsubscribing and setting unmounted');
      isMounted.current = false;
      if (authListener?.unsubscribe) {
        authListener.unsubscribe();
      } else {
        console.warn('[AuthContext] Could not unsubscribe from auth listener.');
      }
    };
  }, [localPreviewMode]);

  const signInWithGoogle = async ({ forceAccountSelection = false } = {}) => {
    console.log('[AuthContext] Attempting Google Sign In...');
    if (localPreviewMode) {
      writeCachedProfile(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      return;
    }
    safeSetLoading(true);
    try {
      if (!isSupabaseConfigured) {
        throw new Error('Supabase auth is not configured in the frontend. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel, then redeploy.');
      }

      setAuthFlowMarker();
      const queryParams = forceAccountSelection ? { prompt: 'select_account' } : undefined;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback',
          ...(queryParams ? { queryParams } : {})
        }
      });
      if (error) throw error;
      console.log('[AuthContext] Redirecting to Google...');
    } catch (error) {
      console.error('Error signing in with Google:', error);
      alert(`Error signing in with Google: ${error.message}`);
      safeSetLoading(false);
    }
  };

  const signIn = async (email, password) => {
    console.log(`[AuthContext] Attempting email/password sign in for: ${email}`);
    if (localPreviewMode) {
      writeCachedProfile(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      return;
    }
    safeSetLoading(true);
    try {
      if (!isSupabaseConfigured) {
        throw new Error('Supabase auth is not configured in the frontend. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel, then redeploy.');
      }

      setAuthFlowMarker();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      clearSignedOutAt();
      clearAuthFlowMarker();
      console.log('[AuthContext] Email/password sign in successful (pending state update)');
    } catch (error) {
      console.error('Error signing in:', error);
      alert(`Error signing in: ${error.message}`);
      safeSetLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    console.log('[AuthContext] Attempting Sign Out...');
    const currentUserId = authStateRef.current.user?.id || null;
    if (localPreviewMode) {
      clearSupabaseAuthStorage(supabaseProjectRef);
      clearAuthFlowMarker();
      clearPrePaymentMarkers();
      clearPaymentFlags();
      setSignedOutAt();
      writeCachedProfile(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      return;
    }
    safeSetLoading(true);
    let signOutError = null;

    try {
      console.log('[AuthContext] Clearing auth state in UI...');
      if (currentUserId) {
        clearAllApBioOnboardingStorage(currentUserId);
      }
      clearAuthState();
      clearAuthRedirectFlags();

      console.log('[AuthContext] Calling supabase.auth.signOut()...');
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('[AuthContext] Error from Supabase during signOut:', error);
        throw error;
      }

      console.log('[AuthContext] Sign out completed successfully');
    } catch (error) {
      console.error('[AuthContext] Error signing out:', error);
      signOutError = error;
    } finally {
      console.log('[AuthContext] Clearing Supabase auth storage...');
      clearSupabaseAuthStorage(supabaseProjectRef);
      clearAuthFlowMarker();
      clearPrePaymentMarkers();
      clearPaymentFlags();
      setSignedOutAt();
      broadcastAuthChange('logout');
      safeSetLoading(false);
    }

    if (signOutError) {
      const isNormalSignOutError = signOutError.message && (
        signOutError.message.toLowerCase().includes('session missing') ||
        signOutError.message.toLowerCase().includes('session not found') ||
        signOutError.message.toLowerCase().includes('no session') ||
        signOutError.message.toLowerCase().includes('invalid session')
      );

      if (!isNormalSignOutError) {
        alert(`Error signing out: ${signOutError.message}`);
        throw signOutError;
      }
    }
  };

  const resetPassword = async (email) => {
    console.log(`[AuthContext] Attempting password reset for: ${email}`);
    if (localPreviewMode) {
      alert('Local Preview mode skips password reset because auth is synthetic.');
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/update-password'
      });
      if (error) throw error;
      alert('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      alert(`Error sending password reset email: ${error.message}`);
    }
  };

  const refreshProfileCache = async (userId = null, options = {}) => {
    const targetUserId = userId || user?.id;
    if (!targetUserId) {
      console.warn('[AuthContext] refreshProfileCache called without a user ID.');
      return null;
    }

    const { onProfileBootstrapEvent } = options;

    if (localPreviewMode && isPreviewUserId(targetUserId)) {
      writeCachedProfile(targetUserId, LOCAL_PREVIEW_PROFILE);
      if (targetUserId === user?.id) {
        safeSetProfile(LOCAL_PREVIEW_PROFILE);
      }
      return LOCAL_PREVIEW_PROFILE;
    }

    console.log(`[AuthContext] Forcing profile cache refresh for user: ${targetUserId}`);
    clearCachedProfileForUser(targetUserId);

    const currentUserId = authStateRef.current.user?.id;
    const hydrationToken = currentUserId === targetUserId
      ? invalidateHydration(targetUserId)
      : activeHydrationRef.current;

    if (currentUserId === targetUserId) {
      safeSetProfileStatus('loading');
    }

    try {
      const refreshedProfile = await getOrCreateProfile(targetUserId, user, {
        forceRefresh: true,
        onProfileBootstrapEvent
      });

      if (
        targetUserId === currentUserId &&
        isMounted.current &&
        isHydrationCurrent(targetUserId, hydrationToken.generation)
      ) {
        safeSetProfile(refreshedProfile || null);
        safeSetProfileStatus(refreshedProfile ? 'ready' : 'error');
      }

      return refreshedProfile || null;
    } catch (error) {
      console.error(`[AuthContext] Error during forced profile refresh for ${targetUserId}:`, error);
      if (
        targetUserId === currentUserId &&
        isMounted.current &&
        isHydrationCurrent(targetUserId, hydrationToken.generation)
      ) {
        safeSetProfile(null);
        safeSetProfileStatus('error');
      }
      return null;
    }
  };

  const signUp = async (email, password, subscriptionType = 'free') => {
    console.log(`[AuthContext] Attempting sign up for: ${email}, plan: ${subscriptionType}`);
    if (localPreviewMode) {
      writeCachedProfile(LOCAL_PREVIEW_USER.id, LOCAL_PREVIEW_PROFILE);
      safeSetUser(LOCAL_PREVIEW_USER);
      safeSetProfile(LOCAL_PREVIEW_PROFILE);
      safeSetSubscription(LOCAL_PREVIEW_SUBSCRIPTION);
      safeSetRemainingPrompts(LOCAL_PREVIEW_SUBSCRIPTION.remaining_prompts);
      safeSetProfileStatus('ready');
      safeSetSubscriptionStatus('ready');
      safeSetLoading(false);
      return;
    }
    safeSetLoading(true);
    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      });

      if (signUpError) throw signUpError;

      console.log('[AuthContext] Sign up successful (pending state update/email confirmation)', signUpData);
      alert('Sign up successful! Please check your email to confirm your account.');
    } catch (error) {
      console.error('Error signing up:', error);
      alert(`Error signing up: ${error.message}`);
      safeSetLoading(false);
      throw error;
    }
  };

  const markCallbackHydration = (userId, { subscription: cbSubscription } = {}) => {
    recentCallbackHydrationRef.current = { userId, at: Date.now(), subscription: cbSubscription ?? null };
  };

  const value = {
    user,
    profile,
    loading,
    subscription,
    remainingPrompts,
    accessState,
    profileStatus,
    subscriptionStatus,
    signIn,
    signInWithGoogle,
    signOut,
    signUp,
    resetPassword,
    refreshProfileCache,
    fetchUserSubscription,
    markCallbackHydration
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
